"use client";

import { cn } from "@/lib/utils";
import type { AiProvider } from "@/lib/ai-engine/providers";
import type { ApiKeyProvider } from "@/types";

interface ProviderLogoProps {
  provider: AiProvider | ApiKeyProvider | string;
  className?: string;
  format?: "svg" | "png";
}

const LOGO_PATHS: Record<string, string> = {
  gemini: "/logos/providers/gemini.png",
  openai: "/logos/providers/openai.png",
  anthropic: "/logos/providers/anthropic.png",
  claude: "/logos/providers/anthropic.png",
  deepseek: "/logos/providers/deepseek.png",
  elevenlabs: "/logos/providers/elevenlabs.png",
};

const LOGO_DARK_PATHS: Partial<Record<string, string>> = {
  openai: "/logos/providers/openai-dark.png",
  elevenlabs: "/logos/providers/elevenlabs-dark.png",
};

export function ProviderLogo({
  provider,
  className = "size-4",
}: ProviderLogoProps) {
  const src = LOGO_PATHS[provider];
  const darkSrc = LOGO_DARK_PATHS[provider];

  if (!src) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded bg-orange-500/15 font-bold text-orange-400 text-[11px] leading-none",
          className,
        )}
        aria-label={`${provider} logo`}
      >
        AI
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden",
        className,
      )}
    >
      {/* 2026-09-25 FASE 4 transversal oferta 9 perf: width/height anti-CLS + loading lazy + decoding async.
          2026-10-31 Iconos oficiales raster descargados: adaptación impecable para modos claro y oscuro sin filtros. */}
      {darkSrc ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={`${provider} logo`}
            className="size-full object-contain dark:hidden"
            loading="lazy"
            decoding="async"
            width={24}
            height={24}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={darkSrc}
            alt={`${provider} logo`}
            className="hidden size-full object-contain dark:block"
            loading="lazy"
            decoding="async"
            width={24}
            height={24}
          />
        </>
      ) : (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={src}
          alt={`${provider} logo`}
          className="size-full object-contain"
          loading="lazy"
          decoding="async"
          width={24}
          height={24}
        />
      )}
    </span>
  );
}
